import { Box, Button, Card, CardContent, CardHeader, Divider, Grid, MenuItem } from "@mui/material"
import { get, isEqual, omitBy } from "lodash"
import { forwardRef, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import {
    useGetWarehouseByIdQuery,
    useGetWarehousesByAdminQuery,
    useGetWarehousesQuery
} from "src/_api/warehouseApi"
import ServerAutoCompleteSearch from "../../Inputs/ServerAutoCompleteSearch"

const WAREHOUSES_PAGE_SIZE = 20

/**
 * Server paginated / server searched drop-in replacement for `AutoCompletedCard`
 * when the options are warehouses.
 *
 * Renders the exact same card + autocomplete UI, but instead of pulling the whole
 * list at once it fetches one page at a time (`page`/`limit`), loads the next page
 * on scroll and delegates the search to the server (`searchKey`).
 *
 * `value` is the warehouse id (or the warehouse object); the selected option is
 * resolved from the loaded pages and, when it isn't part of them, fetched by id.
 */
export default function WarehouseAutocompleteCard({
    handleChange,
    cardTitle,
    value,
    label,
    error,
    required = false,
    disabled = false,
    // "merchant" -> /warehouse , "admin" -> /warehouse/filter-by-admin
    source = "merchant",
    role,
    merchant = null,
    skip = false,
    resolveSelectedById,
    openDialog = () => { },
    addNewRecord = false,
    addRecordLabel = "",
    menuOptionName = "name",
    getOptionLabel = (option) => (option && option.name) ? option.name : '',
    ...props
}) {
    const { t } = useTranslation()

    const isAdmin = source === "admin"
    // The admin endpoint is merchant scoped, so a warehouse can't be fetched by id there.
    const canResolveById = resolveSelectedById ?? !isAdmin

    const [params, setParams] = useState({ page: 1, limit: WAREHOUSES_PAGE_SIZE, searchKey: "" })
    const [warehouses, setWarehouses] = useState([])
    const [hasNextPage, setHasNextPage] = useState(true)

    const merchantResult = useGetWarehousesQuery(
        omitBy(params, v => !v),
        { skip: skip || isAdmin }
    )
    const adminResult = useGetWarehousesByAdminQuery(
        omitBy({ ...params, role, merchant }, v => !v),
        { skip: skip || !isAdmin }
    )

    const { data, isLoading, isFetching } = isAdmin ? adminResult : merchantResult

    // Reset the accumulated pages whenever the query scope changes
    const scope = useMemo(() => ({ source, role, merchant, skip }), [source, role, merchant, skip])
    const scopeRef = useRef(scope)
    useEffect(() => {
        if (isEqual(scopeRef.current, scope)) return
        scopeRef.current = scope
        setWarehouses([])
        setHasNextPage(true)
        setParams((prev) => ({ ...prev, page: 1, searchKey: "" }))
    }, [scope])

    // Merge the incoming page into the already loaded ones, deduped by id
    useEffect(() => {
        if (!data) return
        setWarehouses((prev) => {
            if (data?.page === 1) return data?.docs ?? []

            const previous = prev ?? []
            const previousIds = new Set(previous.map((warehouse) => warehouse?.id))
            const incoming = (data?.docs ?? []).filter((warehouse) => !previousIds.has(warehouse?.id))
            return [...previous, ...incoming]
        })
        setHasNextPage(!!data?.hasNextPage)
    }, [data])

    const valueId = (value && typeof value === "object") ? value?.id : value

    const [selected, setSelected] = useState(null)

    useEffect(() => {
        if (!valueId) {
            setSelected(null)
            return
        }

        const loaded = warehouses?.find((warehouse) => warehouse?.id === valueId)
        if (loaded) {
            setSelected(loaded)
            return
        }

        setSelected((prev) => {
            // Already resolved (from a previous page or fetched by id)
            if (prev?.id === valueId) return prev
            // The parent handed us the whole warehouse, no need to resolve it
            if (value && typeof value === "object" && value?.id === valueId) return value
            return null
        })
    }, [valueId, warehouses, value])

    // The selected warehouse may live outside the loaded pages, fetch it on its own
    const { data: selectedWarehouse } = useGetWarehouseByIdQuery(valueId, {
        skip: !valueId || !!selected?.id || !canResolveById || skip
    })

    useEffect(() => {
        if (!!selectedWarehouse?.id && selectedWarehouse?.id === valueId) {
            setSelected(selectedWarehouse)
        }
    }, [selectedWarehouse, valueId])

    // The selected warehouse isn't necessarily part of the loaded pages (or of the
    // current search results), keep it listed so it stays visible and selected
    const options = useMemo(() => {
        const loaded = warehouses ?? []
        if (!selected?.id || loaded.some((warehouse) => warehouse?.id === selected?.id)) {
            return loaded
        }
        return [selected, ...loaded]
    }, [warehouses, selected])

    // Keep the listbox component identity stable so the popup isn't remounted while
    // the user scrolls through the pages
    const openDialogRef = useRef(openDialog)
    openDialogRef.current = openDialog
    const addRecordLabelRef = useRef(addRecordLabel)
    addRecordLabelRef.current = addRecordLabel

    const AddNewRecord = useMemo(() => forwardRef(function AddNewRecord({ children, ...other }, ref) {
        return (
            <Box {...other} ref={ref}>
                <Button
                    sx={{ textTransform: "none" }}
                    fullWidth
                    onClick={() => openDialogRef.current()}
                >
                    {t(addRecordLabelRef.current)}
                </Button>
                {children}
            </Box>
        )
    }), [t])

    return (
        <Card>
            <CardHeader title={t(cardTitle)} />
            <Divider />
            <CardContent>
                <Grid container>
                    <Grid item xs={12}>
                        <ServerAutoCompleteSearch
                            fullWidth
                            disabled={disabled}
                            value={selected}
                            options={options}
                            isOptionEqualToValue={(option, selectedOption) => option?.id === selectedOption?.id}
                            getOptionLabel={getOptionLabel}
                            loading={isLoading || isFetching}
                            loadingText={t("loading data...")}
                            label={t(label)}
                            error={error}
                            required={required}
                            hasNextPage={hasNextPage}
                            onLoadMore={() => setParams((prev) => ({ ...prev, page: prev.page + 1 }))}
                            onOpen={() => {
                                // Reopening after a search left the list filtered, restore the full list
                                if (params.searchKey) {
                                    setWarehouses([])
                                    setParams((prev) => ({ ...prev, page: 1, searchKey: "" }))
                                }
                            }}
                            onInputChange={(searchValue) => {
                                setWarehouses([])
                                setParams((prev) => ({ ...prev, page: 1, searchKey: searchValue ?? "" }))
                            }}
                            onChange={(newValue) => {
                                setSelected(newValue ?? null)
                                handleChange(null, newValue)
                            }}
                            renderOption={(optionProps, option) => (
                                <MenuItem
                                    {...optionProps}
                                    key={option.id}
                                >
                                    {get(option, menuOptionName)}
                                </MenuItem>
                            )}
                            ListboxComponent={addNewRecord ? AddNewRecord : undefined}
                            {...props}
                        />
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    )
}
